// =============================================================================
// domain/tradeForm.ts — the trade form's arithmetic, as pure functions.
// -----------------------------------------------------------------------------
// The old client's trade window derives eleven displayed numbers from six typed
// ones, and does it inside DOM handlers reading `#t-rpts` by id. That is exactly
// the code a rebuild should not carry across: every rule below was reachable only
// by opening a modal and typing into it.
//
// Ported from `recalcDerived`, `computeFromRiskPlan`, `computeFromStop`,
// `computeFromTarget`, `excursionStrings`, `commitTrade`'s validation, and the
// `REQ` completeness list.
//
// ── 🚨 THE RISK PLAN IS A THREE-WAY BINDING, NOT A FORM ──
//
// `Risk (pts)`, `R:R Planned`, `Planned Stop` and `Planned Target` are four
// inputs over two degrees of freedom. The old client resolves it with THREE
// flows, and which one runs depends on which field the user touched:
//
//   forward   risk + R:R + entry + side  →  stop, target
//   reverse   stop typed                 →  risk, then target
//   reverse   target typed               →  R:R
//
// Implemented as separate functions rather than one solver on purpose: a single
// "recompute everything" pass has no way to know which field is the input, so it
// overwrites the number the user is typing into. The old client's `set()` helper
// carries a `document.activeElement !== el` guard for precisely that reason —
// this shape does not need the guard, because the flow that runs never writes the
// field that triggered it.
//
// ── 🚨 `rrField` IS NOT `rrPlanned` ──
//
// The R:R input has a unit toggle: `R` (a ratio) or `pts` (a reward distance).
// What is STORED is always the canonical ratio. Conflating the two is a silent
// 3× error in every R-denominated metric on a trade with 3-point risk, so the
// field value and the stored value have different names here and
// `storedRrPlanned` is the only bridge.
// =============================================================================

import { sessionFromTime } from './session';
import type { TradeSide } from './types';

// ---------------------------------------------------------------------------
// Values
// ---------------------------------------------------------------------------

/** Which unit the R:R input is currently expressed in. `appFlags.plannedTargetUnit`. */
export type PlannedTargetUnit = 'r' | 'points';

export const TRADE_FORM_TABS = [
  ['ov', 'Overview'],
  ['ex', 'Execution'],
  ['rk', 'Risk'],
  ['su', 'Setup'],
  ['pr', 'Process'],
  ['nm', 'Notes & Media'],
] as const;

export type TradeFormTab = (typeof TRADE_FORM_TABS)[number][0];

/**
 * Every value the form OWNS.
 *
 * ⚠ This list is also the save's column list (see `io/sync/saveTrade.ts`). A
 * field that is not here is not written, and a field that is here must be
 * rendered — D61: a field the form sends but never showed is a blank the user
 * unknowingly authorised.
 */
export interface TradeFormValues {
  accountId: string;
  date: string;
  contract: string;
  side: TradeSide;
  qty: number;
  timeIn: string;
  timeOut: string;
  entry: number;
  exit: number;
  fees: number;
  /**
   * Whether the commission schedule may still heal this trade's fee.
   *
   * ⚠ Owned by the form but has NO CONTROL of its own: typing in the Fees field
   * flips it to `false`, exactly as the old client does. Without that,
   * `selectors/derive.ts` keeps overwriting a commission the user corrected by
   * hand on every read, and the correction looks like it never saved.
   */
  feesAuto: boolean;

  riskPoints: number;
  /** ⚠ In the CURRENT unit — a ratio under `r`, a points distance under `points`. */
  rrField: number;
  plannedStop: number;
  plannedTarget: number;

  patternId: string;
  exitModelId: string;
  grade: string;
  sessionLabel: string;
  marketRegime: string;
  newsEvent: string;

  notes: string;
  /**
   * Storage object names for this trade's attachments.
   *
   * ⚠ THEY BELONG HERE ONLY BECAUSE THE NOTES TAB RENDERS THEM. Both were
   * deliberately outside this type while the tab showed a count and an apology —
   * a field in `TradeFormValues` is a field the save writes, and writing a list
   * the form never displayed is exactly the D61 blank-over-real-data case. They
   * moved in with the gallery, not before it.
   *
   * The form does not upload or delete: `ui/media` uploads and hands back a name,
   * and a removal drops the reference while leaving the object in the bucket
   * (there is no backup, so nothing in this client deletes one).
   */
  screenshots: readonly string[];
  recordings: readonly string[];
  booleans: Readonly<Record<string, boolean>>;
  mistakeTags: readonly string[];
  tags: readonly string[];
  /** Flat ids, parents and children — see `domain/confluences.ts`. */
  confluences: ReadonlySet<string>;
}

// ---------------------------------------------------------------------------
// Derivation
// ---------------------------------------------------------------------------

export interface TradeFormDerived {
  points: number;
  grossPnl: number;
  netPnl: number;
  /** Null when it cannot be computed — NOT 0. `$0 of risk` is a different claim. */
  riskAtEntry: number | null;
  rrRealized: number | null;
}

/**
 * The auto fields. `dollarsPerPoint` comes from the contracts catalogue.
 *
 * ⚠ `riskAtEntry` and `rrRealized` are NULL rather than 0 when their inputs are
 * missing, and the old client agrees — it writes `''` into both disabled inputs.
 * A zero would read as "you risked nothing", which on a trade with no stop
 * recorded is the opposite of the truth.
 */
export function deriveTrade(v: TradeFormValues, dollarsPerPoint: number): TradeFormDerived {
  const points = v.side === 'long' ? v.exit - v.entry : v.entry - v.exit;
  const grossPnl = points * dollarsPerPoint * v.qty;
  return {
    points,
    grossPnl,
    netPnl: grossPnl - v.fees,
    riskAtEntry: v.riskPoints && dollarsPerPoint && v.qty
      ? v.riskPoints * dollarsPerPoint * v.qty
      : null,
    // ⚠ Gated on `exit && entry`, matching the old client: an open position has
    // no realized R, and dividing its 0 points by the risk would print "0.00R" —
    // indistinguishable from a scratch.
    rrRealized: v.riskPoints && v.exit && v.entry ? points / v.riskPoints : null,
  };
}

/**
 * What actually goes in the `rr_planned` column: always the canonical RATIO.
 *
 * ⚠ 4dp, matching the old client. `reward/risk` on 8 points over 3 is
 * 2.666… and rounding it to 2dp then multiplying back gives a target 0.02 points
 * off — enough to show a stale price beside a fresh ratio on the same tab.
 */
export function storedRrPlanned(v: TradeFormValues, unit: PlannedTargetUnit): number {
  if (unit === 'points') return v.riskPoints ? Number((v.rrField / v.riskPoints).toFixed(4)) : 0;
  return v.rrField;
}

/** What `defaultRiskByInstrument` holds for one contract. */
export interface DefaultRisk {
  riskPoints: number;
  rrPlanned: number | null;
}

/**
 * The user's configured default risk for an instrument, or null.
 *
 * ⚠ THERE IS NO PLATFORM DEFAULT, and the old client says why in as many words:
 * *"NO magic 4-pt default at the platform level — every user has their own risk
 * methodology"*. A fabricated denominator produces an R-multiple that looks
 * measured, on every screen that reads it.
 *
 * ⚠ `riskPoints <= 0` is rejected rather than passed through: it would divide
 * into `rrRealized` and the excursion R clause.
 */
export function defaultRiskFor(
  contract: string, table: Record<string, unknown> | null | undefined,
): DefaultRisk | null {
  if (!contract || !table) return null;
  const def = table[contract.toUpperCase()];
  if (!def || typeof def !== 'object') return null;
  const r = def as Record<string, unknown>;
  const riskPoints = Number(r['riskPoints']);
  const rrPlanned = Number(r['rrPlanned']);
  if (!Number.isFinite(riskPoints) || riskPoints <= 0) return null;
  return {
    riskPoints,
    rrPlanned: Number.isFinite(rrPlanned) && rrPlanned > 0 ? rrPlanned : null,
  };
}

export interface StoredRiskPlan {
  riskPoints: number | null;
  rrPlanned: number | null;
  plannedStop: number | null;
  plannedTarget: number | null;
}

/**
 * The risk and R:R the form OPENS with — the old client's three-step fallback.
 *
 *   riskPoints:  stored → |entry − plannedStop| → the user's per-instrument default
 *   rrPlanned:   stored → the user's per-instrument default
 *
 * ⚠ THE DEFAULT STEP IS NOT COSMETIC. It is what made beta's screenshot of Josh's
 * trade show `Planned R 2.00` and `Realized R 3.42` while the port showed em
 * dashes on the same row: the trade carries no risk at all, and beta was filling
 * both fields from `defaultRiskByInstrument`. Realized R is `points ÷ risk`, so
 * with no risk seeded there is no realized R either — one missing setting blanked
 * two numbers and added a seventh item to the annotation badge.
 */
export function seedRiskPlan(
  stored: StoredRiskPlan, entry: number | null, def: DefaultRisk | null,
): { riskPoints: number; rrPlanned: number } {
  let riskPoints = Number(stored.riskPoints) || 0;
  const e = Number(entry) || 0;
  if (!riskPoints && stored.plannedStop && e) riskPoints = Math.abs(e - Number(stored.plannedStop));
  if (!riskPoints && def) riskPoints = def.riskPoints;
  const rrPlanned = Number(stored.rrPlanned) || (def?.rrPlanned ?? 0);
  return { riskPoints, rrPlanned };
}

/**
 * Seed the R:R INPUT from the resolved plan.
 *
 * ⚠ Under `points` the reward distance is taken from the stored TARGET PRICE
 * first, and only falls back to `rr × risk`. The old client's reason is worth
 * keeping: the target price is robust even when risk is blank, and a form that
 * derived the distance from a missing risk would show an empty reward beside a
 * real target on the same row.
 */
export function seedRrField(
  stored: { rrPlanned: number | null; plannedTarget: number | null; riskPoints: number | null },
  entry: number | null,
  unit: PlannedTargetUnit,
): number {
  const rr = stored.rrPlanned ?? 0;
  if (unit !== 'points') return rr;
  const e = Number(entry) || 0;
  const tgt = Number(stored.plannedTarget) || 0;
  if (e && tgt) return Number(Math.abs(tgt - e).toFixed(2));
  const rp = Number(stored.riskPoints) || 0;
  return rr && rp ? Number((rr * rp).toFixed(2)) : 0;
}

// ---------------------------------------------------------------------------
// The three risk-plan flows
// ---------------------------------------------------------------------------

/**
 * Round a price to the instrument's tick.
 *
 * ⚠ The decimal clamp is NOT in the old client, and it is a fix rather than a
 * divergence: `Math.round(v / 0.01) * 0.01` returns 7473.250000000001 in IEEE
 * doubles, and the old client writes that string straight into the Planned Stop
 * input. Snapping to the tick's own precision produces the value the old client
 * MEANT everywhere it already produces a clean one.
 */
export function roundToTick(v: number, tickSize: number | null): number {
  const tick = tickSize && tickSize > 0 ? tickSize : 0.01;
  const dp = decimalsFor(tick);
  return Number((Math.round(v / tick) * tick).toFixed(dp));
}

function decimalsFor(tick: number): number {
  const s = String(tick);
  const dot = s.indexOf('.');
  return dot < 0 ? 0 : Math.min(8, s.length - dot - 1);
}

/** FORWARD: risk + R:R + entry + side → stop and target. */
export function fromRiskPlan(
  v: TradeFormValues, unit: PlannedTargetUnit, tickSize: number | null,
): { plannedStop?: number; plannedTarget?: number } {
  const out: { plannedStop?: number; plannedTarget?: number } = {};
  if (v.entry && v.riskPoints) {
    const stop = v.side === 'long' ? v.entry - v.riskPoints : v.entry + v.riskPoints;
    out.plannedStop = roundToTick(stop, tickSize);
  }
  if (v.entry && v.rrField) {
    // ⚠ Under `points` the field IS the reward distance, so the target price
    // HOLDS when risk changes. Under `r` it is a multiple and needs risk — with
    // no risk there is nothing to multiply, and inventing one would move the
    // target the user can see.
    const rewardPts = unit === 'points' ? v.rrField : (v.riskPoints ? v.riskPoints * v.rrField : 0);
    if (rewardPts) {
      const target = v.side === 'long' ? v.entry + rewardPts : v.entry - rewardPts;
      out.plannedTarget = roundToTick(target, tickSize);
    }
  }
  return out;
}

/** REVERSE: the user typed a stop → back-compute risk, then re-derive the target. */
export function fromStop(
  v: TradeFormValues, unit: PlannedTargetUnit, tickSize: number | null,
): { riskPoints?: number; plannedTarget?: number } {
  const out: { riskPoints?: number; plannedTarget?: number } = {};
  // ⚠ `entry !== stop` guards a zero-risk plan. Without it a stop typed at the
  // entry price gives riskPoints 0, which then divides into `rrRealized` and
  // `riskAtEntry` — the old client carries the same guard.
  if (v.entry && v.plannedStop && v.entry !== v.plannedStop) {
    const riskPoints = Number(Math.abs(v.entry - v.plannedStop).toFixed(2));
    out.riskPoints = riskPoints;
    if (v.rrField) {
      const rewardPts = unit === 'points' ? v.rrField : riskPoints * v.rrField;
      const target = v.side === 'long' ? v.entry + rewardPts : v.entry - rewardPts;
      out.plannedTarget = roundToTick(target, tickSize);
    }
  }
  return out;
}

/**
 * The Session the picker OPENS on: the stored label, else the one derived from
 * the entry time.
 *
 * ⚠ The fallback is the old client's, and it is visible in Josh's Setup
 * screenshot: that trade stores no session, and beta shows **Asia** because
 * 18:01 ET is past the 18:00 Asia open. The port showed "—" until this landed.
 *
 * ⚠ It is a real DEFAULT, not just a display: `commitTrade` stores
 * `picked || derived`, so opening the form and saving writes the derived label.
 * That is deliberate in the old client — the session is a fact about the clock,
 * not an opinion — and `resolveSessionLabel` keeps `sessionAuto` true for it, so
 * a later timezone change can still re-derive it.
 */
export function seedSessionLabel(
  stored: string, date: string, timeIn: string, tz: string,
): string {
  return stored || sessionFromTime(timeIn || null, date || null, tz);
}

/**
 * The pass the old client runs ONCE when the form opens.
 *
 *   if there is risk        → forward flow, filling Stop and Target
 *   else if there is a stop → reverse flow, filling Risk and Target
 *   else                    → nothing to derive
 *
 * ⚠ NOT optional, and its absence is invisible in the code. Josh's trade stores
 * no stop and no target; beta's Risk tab shows 7473.25 and 7482.25 because this
 * pass derived them from the seeded risk on open. Without it the port showed two
 * empty inputs labelled "(auto)" beside a filled Risk — which reads as a broken
 * auto-fill, and is the second defect on this tab that only a side-by-side
 * against the real screenshot could surface.
 */
export function applyInitialRiskFlow(
  v: TradeFormValues, unit: PlannedTargetUnit, tickSize: number | null,
): TradeFormValues {
  if (v.riskPoints) return { ...v, ...fromRiskPlan(v, unit, tickSize) };
  if (v.plannedStop) return { ...v, ...fromStop(v, unit, tickSize) };
  return v;
}

/** REVERSE: the user typed a target → back-compute the R:R field. */
export function fromTarget(v: TradeFormValues, unit: PlannedTargetUnit): { rrField?: number } {
  if (!v.entry || !v.plannedTarget) return {};
  const rewardPts = v.side === 'long' ? v.plannedTarget - v.entry : v.entry - v.plannedTarget;
  if (unit === 'points') return { rrField: Number(rewardPts.toFixed(2)) };
  if (!v.riskPoints) return {};
  return { rrField: Number((rewardPts / v.riskPoints).toFixed(2)) };
}

/**
 * Convert the R:R field when the unit toggle flips.
 *
 * ⚠ With a value but NO RISK the field is CLEARED, not left alone. The old
 * client's comment says why: there is nothing to convert with, and leaving "2"
 * in place after switching to points silently reinterprets a 2:1 target as a
 * 2-point one.
 */
export function convertRrField(
  rrField: number, riskPoints: number, to: PlannedTargetUnit,
): number {
  if (!rrField) return rrField;
  if (!riskPoints) return 0;
  return to === 'points'
    ? Number((rrField * riskPoints).toFixed(2))
    : Number((rrField / riskPoints).toFixed(2));
}

// ---------------------------------------------------------------------------
// Excursion (read-only, but it MOVES)
// ---------------------------------------------------------------------------

export interface ExcursionDisplay {
  mae: string;
  mfe: string;
  captured: string;
  /** Null when there is no MFE to capture against. Negative renders red. */
  capturedPct: number | null;
  timing: string;
}

const n2 = (x: number) => x.toFixed(2);

/**
 * The old client's `fmtMoney`: SIGNED, and the sign sits outside the `$`.
 *
 * ⚠ Reimplemented here rather than imported from `ui/format.ts` because `domain/`
 * may not import `ui/` — and `money()` there is deliberately a different
 * function. Josh's screenshot pins this one exactly: `-$3.75` for the MAE and
 * `+$327.50` for the MFE, so a plus on the favourable side is not decoration, it
 * is the string beta prints.
 */
const usd = (x: number) => {
  const sign = x < 0 ? '-' : x > 0 ? '+' : '';
  const s = Math.abs(x).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${sign}$${s}`;
};

/** Whole seconds → `M:SS`. */
export function formatExcursionDuration(secs: number): string {
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
}

/**
 * The three excursion strings and the timing line.
 *
 * ⚠ ONLY THE POINTS ARE FACTS. `$` tracks qty and contract, `R` tracks the risk
 * the user is typing, and `MFE Captured` tracks the exit — so all three are
 * recomputed on every edit rather than rendered once. The old client's note:
 * they must "never drift from Risk $ at Entry / R:R Realized sitting directly
 * above them".
 *
 * ⚠ MAE and MFE are stored as absolute MAGNITUDES, so the signs here are
 * literal: MAE always prints `−`, MFE always prints `+`.
 */
export function excursionDisplay(args: {
  maePoints: number | null;
  mfePoints: number | null;
  maeSecs: number | null;
  mfeSecs: number | null;
  riskPoints: number | null;
  dollarsPerPoint: number;
  qty: number;
  realizedPoints: number | null;
}): ExcursionDisplay {
  const { maePoints: mae, mfePoints: mfe, riskPoints: rp, dollarsPerPoint: dpp, qty } = args;

  const maeStr = mae != null
    ? `−${n2(mae)} pts · ${usd(-(mae * dpp * qty))}${rp ? ` · −${n2(mae / rp)}R` : ''}`
    : '—';
  const mfeStr = mfe != null
    ? `+${n2(mfe)} pts · ${usd(mfe * dpp * qty)}${rp ? ` · +${n2(mfe / rp)}R` : ''}`
    : '—';

  const capturedPct = (mfe != null && mfe !== 0 && args.realizedPoints != null)
    ? (args.realizedPoints / mfe) * 100
    : null;

  const parts: string[] = [];
  if (args.maeSecs != null) parts.push(`MAE @${formatExcursionDuration(args.maeSecs)}`);
  if (args.mfeSecs != null) parts.push(`MFE peak @${formatExcursionDuration(args.mfeSecs)}`);

  return {
    mae: maeStr,
    mfe: mfeStr,
    captured: capturedPct == null ? '—' : `${capturedPct.toFixed(0)}%`,
    capturedPct,
    timing: parts.length ? parts.join('   ·   ') : '— no tick timing recorded for this trade',
  };
}

/**
 * Risk points to denominate excursion in R.
 *
 * ⚠ Falls back to the stop distance, then to NOTHING. The old client is explicit
 * that there is "no magic 4-pt default" — with no risk on file the R clause is
 * simply omitted from the string, because an invented denominator produces an
 * R-multiple that looks measured.
 */
export function excursionRiskPoints(riskPoints: number, entry: number, plannedStop: number): number | null {
  if (riskPoints) return riskPoints;
  if (plannedStop && entry) return Math.abs(entry - plannedStop);
  return null;
}

// ---------------------------------------------------------------------------
// Annotation completeness
// ---------------------------------------------------------------------------

export interface AnnotationRequirement {
  key: string;
  tab: TradeFormTab;
  /** What the overlay and the tooltip call it. */
  label: string;
  test: (v: TradeFormValues) => boolean;
}

/**
 * The six fields the ✓ badge counts, from the old client's `REQ`.
 *
 * ⚠ Mistake tags and the discipline booleans are deliberately NOT here, and both
 * omissions are load-bearing: a clean trade has no mistake and breaches none, so
 * requiring either would leave the badge permanently nagging on the trades that
 * went right. The old client removed the booleans from this list in v2.62 when
 * they became exception flags.
 */
export const ANNOTATION_REQUIREMENTS: readonly AnnotationRequirement[] = [
  { key: 'risk', tab: 'rk', label: 'Risk (pts) and R:R Planned', test: (v) => v.riskPoints > 0 && v.rrField > 0 },
  { key: 'pattern', tab: 'su', label: 'Pattern', test: (v) => !!v.patternId },
  { key: 'exit', tab: 'su', label: 'Exit Model', test: (v) => !!v.exitModelId },
  { key: 'grade', tab: 'su', label: 'Execution Grade', test: (v) => !!v.grade },
  { key: 'confluences', tab: 'su', label: 'Confluences', test: (v) => v.confluences.size > 0 },
  { key: 'notes', tab: 'nm', label: 'Notes', test: (v) => v.notes.trim().length > 0 },
];

export function unfilledRequirements(v: TradeFormValues): readonly AnnotationRequirement[] {
  return ANNOTATION_REQUIREMENTS.filter((r) => !r.test(v));
}

/** Which tabs get a dot. */
export function tabsNeedingAttention(v: TradeFormValues): ReadonlySet<TradeFormTab> {
  return new Set(unfilledRequirements(v).map((r) => r.tab));
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * The one rule that BLOCKS a save, ported verbatim.
 *
 * Pattern (the entry decision) and exit model (the exit execution) travel
 * together — pairing analytics are meaningless with one of them missing. A trade
 * with NEITHER is fine, and deliberately so: that is every freshly imported
 * trade, and blocking those would make the form unusable for its main job.
 *
 * @param hasExitModels whether the catalogue has anything in it — the message
 *        differs, because "pick one" is useless advice when there are none.
 */
export function validateTradeForm(v: TradeFormValues, hasExitModels: boolean): string | null {
  if (v.patternId && !v.exitModelId) {
    return hasExitModels
      ? 'Pick an Exit Model — required when a Pattern is set.'
      : 'Create an Exit Model first (Patterns tab → Exit Models) — one is required when a Pattern is set.';
  }
  return null;
}
